import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Star, RotateCcw, ArrowRight, Home } from "lucide-react";
import Confetti from "./Confetti";
import { sfx } from "../services/soundfx";
import { speak } from "../services/phonicsAudio";

export default function LevelComplete({ stars = 3, onReplay, onNext, onHome, hasNext, newStickers = [] }) {
  useEffect(() => {
    sfx.fanfare();
    const t = setTimeout(() => speak("Great job! You did it!"), 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 no-select" style={{ background: "#0F172A99", backdropFilter: "blur(8px)" }} data-testid="level-complete">
      <Confetti show count={90} />
      <motion.div
        initial={{ scale: 0.6, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 16 }}
        className="relative w-full max-w-md rounded-[2rem] bg-white border-[6px] border-amber-300 shadow-2xl p-7 text-center"
      >
        <h2 className="font-display text-4xl font-black text-amber-500 mb-1">You did it!</h2>
        <p className="font-bold text-slate-500 mb-5">Level complete</p>

        <div className="flex items-center justify-center gap-3 mb-7">
          {[1, 2, 3].map((n) => (
            <motion.div
              key={n}
              initial={{ scale: 0, rotate: -40 }}
              animate={{ scale: n <= stars ? 1.15 : 0.9, rotate: 0 }}
              transition={{ delay: 0.3 + n * 0.25, type: "spring", stiffness: 300 }}
              onAnimationStart={() => n <= stars && setTimeout(() => sfx.star(), 300 + n * 250)}
            >
              <Star
                className="w-16 h-16"
                strokeWidth={2}
                style={{
                  fill: n <= stars ? "#FACC15" : "#E2E8F0",
                  color: n <= stars ? "#F59E0B" : "#CBD5E1",
                }}
              />
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          {newStickers.length > 0 && (
            <div className="mb-1 rounded-2xl bg-amber-50 border-2 border-amber-200 p-3" data-testid="earned-stickers">
              <p className="font-display font-bold text-amber-600 mb-2 text-sm">New sticker{newStickers.length > 1 ? "s" : ""}!</p>
              <div className="flex items-center justify-center gap-3">
                {newStickers.map((s, i) => (
                  <motion.span
                    key={i}
                    initial={{ scale: 0, rotate: -40 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 1 + i * 0.3, type: "spring", stiffness: 260 }}
                    className="text-5xl"
                  >
                    {s.emoji}
                  </motion.span>
                ))}
              </div>
            </div>
          )}
          {hasNext && (
            <button
              data-testid="next-level-btn"
              onClick={() => { sfx.tap(); onNext(); }}
              className="pop-btn flex items-center justify-center gap-2 rounded-2xl bg-green-500 text-white font-display text-xl font-bold py-4 border-4 border-white shadow-[0_6px_0_#15803D] active:shadow-none"
            >
              Next Adventure <ArrowRight strokeWidth={3} />
            </button>
          )}
          <div className="flex gap-3">
            <button
              data-testid="replay-btn"
              onClick={() => { sfx.tap(); onReplay(); }}
              className="pop-btn flex-1 flex items-center justify-center gap-2 rounded-2xl bg-sky-400 text-white font-display text-lg font-bold py-3 border-4 border-white shadow-[0_6px_0_#0284C7] active:shadow-none"
            >
              <RotateCcw strokeWidth={3} /> Replay
            </button>
            <button
              data-testid="home-map-btn"
              onClick={() => { sfx.tap(); onHome(); }}
              className="pop-btn flex-1 flex items-center justify-center gap-2 rounded-2xl bg-amber-400 text-white font-display text-lg font-bold py-3 border-4 border-white shadow-[0_6px_0_#D97706] active:shadow-none"
            >
              <Home strokeWidth={3} /> Map
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
